import { BsdsCard } from '@bsds-lib-component/react';
import React from 'react';

import { Button } from '../..';

export type CompanyApoliceCardData = {
  numeroApolice: string;
  ramo: string;
  cnpj: string;
  papel?: string;
};

type CompanyApoliceCardProps = {
  isSelected: boolean;
  onSelect: () => void;
  data: CompanyApoliceCardData;
  onConfirm?: () => void;
  confirmDisabled?: boolean;
  confirmLabel?: string;
};

const CARD_BASE_CLASS =
  'cursor-pointer transition-all min-h-60 w-80 p-6 duration-200 hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500';
const CARD_SELECTED_CLASS = 'border-red-500 bg-red-50 shadow-lg';
const CARD_UNSELECTED_CLASS = 'border-gray-100 hover:shadow-lg';

type LabeledValueProps = {
  label: string;
  value: string;
  highlight?: boolean;
};

const LabeledValue: React.FC<LabeledValueProps> = ({ label, value, highlight }) => (
  <div className="bsds-width-100 flex flex-col bsds-p-4">
    <div className="bsds-mt-4 bsds-p-4 bg-gray-50/20 rounded-md border border-gray-100 flex flex-col bsds-gap-8">
      <span className="text-sm text-gray-300 font-small">{label}</span>
      <span
        className={
          highlight ? 'text-sm font-bold text-primary-700' : 'text-sm font-semibold text-gray-900'
        }
      >
        {value}
      </span>
    </div>
  </div>
);

const CompanyApoliceCard: React.FC<CompanyApoliceCardProps> = ({
  isSelected,
  onSelect,
  data,
  onConfirm,
  confirmDisabled = false,
  confirmLabel = 'Selecionar e ir para Home',
}) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect();
    }
  };

  const ariaSummary = [
    `Apólice ${data.numeroApolice}`,
    `Ramo ${data.ramo}`,
    `Número do Documento ${data.cnpj}`,
    data.papel ? `Papel ${data.papel}` : null,
  ]
    .filter(Boolean)
    .join(', ');

  const normalizedRamo = data.ramo
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  const ramoBadgeClass =
    normalizedRamo === 'vida'
      ? 'bg-red-200 text-white'
      : normalizedRamo === 'previdencia'
        ? 'bg-yellow-200 text-gray-400'
        : normalizedRamo === 'saude'
          ? 'bg-blue-200 text-white'
          : 'bg-gray-200 text-gray-700';

  return (
    <BsdsCard
      className={`${CARD_BASE_CLASS} ${isSelected ? CARD_SELECTED_CLASS : CARD_UNSELECTED_CLASS}`}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-pressed={isSelected}
      aria-label={ariaSummary}
      platform="web"
    >
      <div className="bsds-flex-direction-column bsds-align-items-start bsds-width-100">
        <div className="bsds-flex-direction-column bsds-width-100 bsds-mb-12 bsds-gap-4">
          <span className="text-sm text-gray-300 text-start shrink-0">Número da Apólice</span>
          <div className="bsds-display-flex bsds-justify-content-between bsds-align-items-start bsds-width-100">
            <span className="text-lg font-semibold text-gray-900 text-start shrink-0">{data.numeroApolice}</span>
            <BsdsCard platform="web" className={`px-3 py-1 rounded-md border-0 shadow-none ${ramoBadgeClass}`}>
              <span className="text-sm font-semibold text-right">{data.ramo}</span>
            </BsdsCard>
          </div>
        </div>

        <hr className="bsds-width-100 border-0 border-t border-gray-200" aria-hidden="true" />

        <div className="bsds-flex-direction-column bsds-align-items-start bsds-width-100 bsds-mt-8">
          <LabeledValue label="Número do Documento" value={data.cnpj} />
          {data.papel != null && data.papel !== '' && <LabeledValue label="Papel" value={data.papel} />}
        </div>

        {isSelected && onConfirm && (
          <div onClick={(e) => e.stopPropagation()} className="bsds-width-100">
            <Button
              color="primary"
              fill="solid"
              onClick={() => void onConfirm()}
              disabled={confirmDisabled}
              className="w-full bsds-mt-8"
            >
              {confirmLabel}
            </Button>
          </div>
        )}
      </div>
    </BsdsCard>
  );
};

type CompanyApoliceCardGridProps = {
  ariaLabel: string;
  children: React.ReactNode;
};

export const CompanyApoliceCardGrid: React.FC<CompanyApoliceCardGridProps> = ({ ariaLabel, children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" role="list" aria-label={ariaLabel}>
    {children}
  </div>
);

export default CompanyApoliceCard;
